import { useState, useMemo, useEffect } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    Avatar,
    Typography,
    Box,
    Paper,
    IconButton,
    Tooltip,
    Chip,
    Switch
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { IProduto } from "../../../../interfaces/IProduto";
import { ICategoria } from "../../../../interfaces/ICategoria";
import { IMarketplace } from "../../../../../marketplace/interfaces/IMarketplace";
import { TipoProduto } from "../../../../enum/TipoProduto";
import { TipoMarketplace, TipoMarketplaceTitulo, TipoMarketplaceCor } from "../../../../../marketplace/enum/TipoMarketplace";
import { useMoeda } from "../../../../../core/hooks/useMoeda";
import { useLetras } from "../../../../../core/hooks/useLetras";
import { useDebounce } from "../../../../../core/hooks/useDebounce";
import { FiltrosTabelaCardapio } from "../FiltrosTabelaCardapio";
import { ModalSelecionarCategoriaAcoes } from "../ModalSelecionarCategoriaAcoes";
import { ExcluirProdutoModal } from "../../../../components/ExcluirProdutoModal";
import { useProdutos } from "../../../../hooks/useProdutos";
import { useNavigate } from "react-router-dom";
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { TabelaContainer } from "./styles";

interface ProdutoComCategoria {
    produto: IProduto;
    categoria: ICategoria;
}

interface TabelaProdutosCardapioProps {
    categorias: ICategoria[];
    marketplaces?: IMarketplace[];
    onAcaoSelecionada: (categoriaUuid: string, acao: 'cadastrar-produto' | 'editar-categoria' | 'excluir-categoria' | 'duplicar-categoria') => void;
    handleRefresh: () => void;
}

export const TabelaProdutosCardapio = ({
    categorias,
    marketplaces,
    onAcaoSelecionada,
    handleRefresh
}: TabelaProdutosCardapioProps) => {
    const { real } = useMoeda();
    const { titulo, maiusculas, removerAcentos, removerCedilha } = useLetras();
    const { excluir, salvar } = useProdutos();
    const navigate = useNavigate();

    const [filtroDescricao, setFiltroDescricao] = useState('');
    const filtroDescricaoDebounced = useDebounce(filtroDescricao, 300);
    const [filtroPrecoMin, setFiltroPrecoMin] = useState('');
    const [filtroPrecoMax, setFiltroPrecoMax] = useState('');
    const [filtroMarketplace, setFiltroMarketplace] = useState('');
    const [filtroCategoria, setFiltroCategoria] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [openModalCategoria, setOpenModalCategoria] = useState(false);
    const [acaoSelecionada, setAcaoSelecionada] = useState<'cadastrar-produto' | 'editar-categoria' | 'excluir-categoria' | 'duplicar-categoria' | null>(null);
    const [produtoExcluir, setProdutoExcluir] = useState<IProduto | undefined>(undefined);
    const [openExcluirProduto, setOpenExcluirProduto] = useState(false);
    const [categoriasLocais, setCategoriasLocais] = useState<ICategoria[]>(categorias);
    const [loadingProdutos, setLoadingProdutos] = useState<Record<string, boolean>>({});

    // Sincronizar categorias locais com props
    useEffect(() => {
        setCategoriasLocais(categorias);
    }, [categorias]);

    // Flatten de todos os produtos com referência à categoria
    const produtosComCategoria: ProdutoComCategoria[] = useMemo(() => {
        const produtos: ProdutoComCategoria[] = [];
        categoriasLocais.forEach(categoria => {
            categoria.produtos?.filter(produto => !produto.apenasCombo).forEach(produto => {
                produtos.push({ produto, categoria });
            });
        });
        return produtos;
    }, [categoriasLocais]);

    // Aplicar filtros
    const produtosFiltrados = useMemo(() => {
        let filtrados = produtosComCategoria;

        // Filtro por descrição (usando debounce)
        if (filtroDescricaoDebounced) {
            const strFiltro = removerAcentos(removerCedilha(maiusculas(filtroDescricaoDebounced)));
            filtrados = filtrados.filter(({ produto }) => {
                const strDescricao = removerAcentos(removerCedilha(maiusculas(produto.descricao)));
                return strDescricao.includes(strFiltro);
            });
        }

        // Filtro por categoria
        if (filtroCategoria) {
            filtrados = filtrados.filter(({ categoria }) => {
                return categoria.uuid === filtroCategoria;
            });
        }

        // Filtro por margem de preço (range)
        if (filtroPrecoMin) {
            const precoMin = parseFloat(filtroPrecoMin);
            if (!isNaN(precoMin)) {
                filtrados = filtrados.filter(({ produto }) => {
                    const preco = produto.preco ?? 0;
                    return preco >= precoMin;
                });
            }
        }

        if (filtroPrecoMax) {
            const precoMax = parseFloat(filtroPrecoMax);
            if (!isNaN(precoMax)) {
                filtrados = filtrados.filter(({ produto }) => {
                    const preco = produto.preco ?? 0;
                    return preco <= precoMax;
                });
            }
        }

        // Filtro por marketplace
        if (filtroMarketplace && marketplaces) {
            filtrados = filtrados.filter(({ categoria }) => {
                return categoria.marketplaces?.some(marketplaceCategoria => {
                    // Buscar o marketplace correspondente na lista de marketplaces
                    const marketplaceEncontrado = marketplaces.find(marketplace => {
                        if ((marketplace.tipo === marketplaceCategoria.tipo) && marketplaceCategoria.tipo === TipoMarketplace.PDV) {
                            return marketplace;
                        }
                        if (marketplace.uuid === marketplaceCategoria.uuid) {
                            return marketplace;
                        }
                        return false;
                    });
                    return marketplaceEncontrado?.uuid === filtroMarketplace;
                });
            });
        }

        return filtrados;
    }, [produtosComCategoria, filtroDescricaoDebounced, filtroPrecoMin, filtroPrecoMax, filtroMarketplace, filtroCategoria, marketplaces, maiusculas, removerAcentos, removerCedilha]);

    // Paginação
    const produtosPaginados = useMemo(() => {
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return produtosFiltrados.slice(startIndex, endIndex);
    }, [produtosFiltrados, page, rowsPerPage]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleAbrirModalCategoria = (acao: 'cadastrar-produto' | 'editar-categoria' | 'excluir-categoria' | 'duplicar-categoria') => {
        setAcaoSelecionada(acao);
        setOpenModalCategoria(true);
    };

    const handleFecharModalCategoria = () => {
        setOpenModalCategoria(false);
        setAcaoSelecionada(null);
    };

    const handleCategoriaSelecionada = (categoriaUuid: string) => {
        if (acaoSelecionada) {
            onAcaoSelecionada(categoriaUuid, acaoSelecionada);
            handleFecharModalCategoria();
        }
    };

    const handleEditarProduto = (produto: IProduto, categoriaUuid: string) => {
        navigate(`/cardapio/categorias/${categoriaUuid}/produtos/${produto.uuid}`);
    };

    const handleAbrirExcluirProduto = (produto: IProduto, categoriaUuid: string) => {
        // Garantir que o produto tenha categoriaUuid para o modal funcionar
        const produtoComCategoria = {
            ...produto,
            categoriaUuid: categoriaUuid
        };
        setProdutoExcluir(produtoComCategoria);
        setOpenExcluirProduto(true);
    };

    const handleFecharExcluirProduto = () => {
        setProdutoExcluir(undefined);
        setOpenExcluirProduto(false);
        setTimeout(() => handleRefresh(), 300);
    };

    const handleExcluirProduto = async (produtoUuid: string) => {
        if (!produtoExcluir) {
            return false;
        }
        // Buscar a categoria do produto
        const produtoComCategoria = produtosComCategoria.find(p => p.produto.uuid === produtoUuid);
        if (!produtoComCategoria?.categoria?.uuid) {
            return false;
        }
        return await excluir(produtoComCategoria.categoria.uuid, produtoUuid);
    };

    const handleToggleStatus = async (produto: IProduto, categoriaUuid: string) => {
        const estaAtivo = !produto.inativadoAs;
        const novoStatus = estaAtivo ? new Date() : undefined;
        
        // Ativar loading
        setLoadingProdutos(prev => ({ ...prev, [produto.uuid]: true }));
        
        try {
            const resultado = await salvar(categoriaUuid, {
                ...produto,
                inativadoAs: novoStatus
            });
            
            // Atualizar estado local se a operação foi bem-sucedida
            if (resultado === true) {
                setCategoriasLocais(prev => prev.map(categoria => {
                    if (categoria.uuid === categoriaUuid) {
                        return {
                            ...categoria,
                            produtos: categoria.produtos?.map(p => 
                                p.uuid === produto.uuid 
                                    ? { ...p, inativadoAs: novoStatus }
                                    : p
                            )
                        };
                    }
                    return categoria;
                }));
            }
        } catch (error) {
            console.error('Erro ao atualizar status do produto:', error);
        } finally {
            // Desativar loading
            setLoadingProdutos(prev => ({ ...prev, [produto.uuid]: false }));
        }
    };

    const getImagemProduto = (produto: IProduto): string => {
        if (produto.imagens?.length && produto.imagens[0]?.url) {
            return produto.imagens[0].url;
        }
        const tipoImagem = produto.tipo === TipoProduto.pizza ? 'pizza' :
            produto.tipo === TipoProduto.industrializado ? 'industrializado' :
            produto.tipo === TipoProduto.preparado ? 'preparado' :
            produto.tipo === TipoProduto.peso ? 'preparado' :
            produto.tipo === TipoProduto.combo ? 'preparado' : 'generico';
        return `${process.env.PUBLIC_URL}/images/produtos/${tipoImagem}.png`;
    };

    return (
        <TabelaContainer>
            <FiltrosTabelaCardapio
                filtroDescricao={filtroDescricao}
                setFiltroDescricao={setFiltroDescricao}
                filtroPrecoMin={filtroPrecoMin}
                setFiltroPrecoMin={setFiltroPrecoMin}
                filtroPrecoMax={filtroPrecoMax}
                setFiltroPrecoMax={setFiltroPrecoMax}
                filtroMarketplace={filtroMarketplace}
                setFiltroMarketplace={setFiltroMarketplace}
                filtroCategoria={filtroCategoria}
                setFiltroCategoria={setFiltroCategoria}
                marketplaces={marketplaces}
                categorias={categorias}
            />
            <Paper sx={{ mt: 2, p: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleAbrirModalCategoria('cadastrar-produto')}
                    >
                        Cadastrar Produto
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => handleAbrirModalCategoria('editar-categoria')}
                    >
                        Editar Categoria
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleAbrirModalCategoria('excluir-categoria')}
                    >
                        Excluir Categoria
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<ContentCopyIcon />}
                        onClick={() => handleAbrirModalCategoria('duplicar-categoria')}
                    >
                        Duplicar Categoria
                    </Button>
                </Box>
                <TableContainer>
                    <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Imagem</TableCell>
                            <TableCell>Descrição</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Valor</TableCell>
                            <TableCell>Valor sem desconto</TableCell>
                            <TableCell>Categoria</TableCell>
                            <TableCell>Marketplaces</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="center">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {produtosPaginados.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center">
                                    <Typography variant="body2" color="text.secondary">
                                        Nenhum produto encontrado
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            produtosPaginados.map(({ produto, categoria }) => {
                                // Buscar marketplaces da categoria
                                const marketplacesCategoria: IMarketplace[] = [];
                                categoria.marketplaces?.forEach(marketplaceCategoria => {
                                    const marketplaceEncontrado = marketplaces?.find(marketplace => {
                                        if ((marketplace.tipo === marketplaceCategoria.tipo) && marketplaceCategoria.tipo === TipoMarketplace.PDV) {
                                            return marketplace;
                                        }
                                        if (marketplace.uuid === marketplaceCategoria.uuid) {
                                            return marketplace;
                                        }
                                        return false;
                                    });
                                    if (marketplaceEncontrado) {
                                        marketplacesCategoria.push(marketplaceEncontrado);
                                    }
                                });

                                // Formatar tipo do produto
                                const tipoProdutoFormatado = produto.tipo === TipoProduto.pizza ? 'Pizza' :
                                    produto.tipo === TipoProduto.industrializado ? 'Industrializado' :
                                    produto.tipo === TipoProduto.preparado ? 'Preparado' :
                                    produto.tipo === TipoProduto.peso ? 'Por peso' :
                                    produto.tipo === TipoProduto.combo ? 'Combo' : produto.tipo;

                                return (
                                    <TableRow key={produto.uuid}>
                                        <TableCell>
                                            <Avatar
                                                src={getImagemProduto(produto)}
                                                alt={produto.descricao}
                                                variant="rounded"
                                                sx={{ width: 56, height: 56 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {titulo(produto.descricao)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {tipoProdutoFormatado}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">
                                                {real(produto.preco)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {produto.precoSemDesconto ? real(produto.precoSemDesconto) : '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {titulo(categoria.descricao)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                {marketplacesCategoria.length > 0 ? (
                                                    marketplacesCategoria.map((marketplace) => (
                                                        <Tooltip key={marketplace.uuid} title={marketplace.nome || marketplace.tipo}>
                                                            <Chip
                                                                size="small"
                                                                label={TipoMarketplaceTitulo[marketplace.tipo] || marketplace.tipo}
                                                                style={{ 
                                                                    background: TipoMarketplaceCor[marketplace.tipo] || '#ccc', 
                                                                    color: "white",
                                                                    fontSize: '0.7rem'
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    ))
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        -
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Switch
                                                checked={!produto.inativadoAs}
                                                onChange={() => handleToggleStatus(produto, categoria.uuid as string)}
                                                size="small"
                                                disabled={loadingProdutos[produto.uuid]}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Editar produto">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditarProduto(produto, categoria.uuid as string)}
                                                    color="primary"
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Excluir produto">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleAbrirExcluirProduto(produto, categoria.uuid as string)}
                                                    color="error"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={produtosFiltrados.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Linhas por página:"
                />
            </Paper>
            <ModalSelecionarCategoriaAcoes
                open={openModalCategoria}
                onClose={handleFecharModalCategoria}
                categorias={categorias}
                onAcaoSelecionada={handleCategoriaSelecionada}
            />
            {produtoExcluir && (
                <ExcluirProdutoModal
                    open={openExcluirProduto}
                    handleClose={handleFecharExcluirProduto}
                    produto={produtoExcluir}
                    excluirProduto={handleExcluirProduto}
                    handleRefresh={handleRefresh}
                />
            )}
        </TabelaContainer>
    );
};

